import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useMyVisitApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import InviteLinkDialog from '../visits/InviteLinkDialog';

const Container = styled.div``;

const StatusChip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

const STATUS_COLORS = {
  waiting: { bg: '#fff4d6', fg: '#8a6d3b' },
  approved: { bg: '#d9f3ea', fg: '#1d9e74' },
  rejected: { bg: '#fde2e2', fg: '#d64545' },
  canceled: { bg: '#ececec', fg: '#666666' },
  expired: { bg: '#ececec', fg: '#666666' },
};

const toId = (v) => (v && typeof v === 'object' ? v.id : v);
const toIds = (arr) => (arr || []).map(toId).filter(Boolean);

export default function MyVisitsPage() {
  const { t } = useTranslation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [busy, setBusy] = useState(false);
  const [inviteDialog, setInviteDialog] = useState({ open: false, link: null, smsSent: false });

  const {
    objectSchema, columns: baseColumns, data, fetching, pagination,
    create, update, cancelVisit, showInvite, statusLabel,
  } = useMyVisitApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({
      ...row,
      gateIds: (row.gates || []).filter((g) => g.gateId).map((g) => g.gateId),
    });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const payload = {
      ...formData,
      gateIds: toIds(formData.gateIds),
    };
    if (selectedRow) {
      const r = await update(selectedRow.id, payload);
      if (r.success) setDrawerOpen(false);
      return r.success;
    }
    const r = await create(payload);
    if (r.success) {
      setDrawerOpen(false);
      // Otomatik onaylanan kayıtta davet bilgisi döner; onaya düşende dönmez
      if (r.data && !r.data.requiresApproval) {
        if (!r.data.smsSent && r.data.inviteLink) {
          setInviteDialog({ open: true, link: r.data.inviteLink, smsSent: false });
        } else if (r.data.smsSent) {
          setInviteDialog({ open: true, link: null, smsSent: true });
        }
      }
    }
    return r.success;
  }, [selectedRow, create, update]);

  const handleShowInvite = useCallback(async (row) => {
    if (row.status !== 'approved') return;
    const res = await showInvite(row.id);
    if (res) setInviteDialog({ open: true, link: res.inviteLink || null, smsSent: !!res.smsSent });
  }, [showInvite]);

  const handleCancelConfirm = useCallback(async () => {
    setBusy(true);
    await cancelVisit(cancelConfirm.id);
    setCancelConfirm({ isOpen: false, id: null, name: '' });
    setBusy(false);
  }, [cancelVisit, cancelConfirm.id]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => [
    { type: 'edit', label: t('visit.edit'), onClick: handleEdit },
    { type: 'copy', label: t('visit.showLink'), condition: (row) => row.status === 'approved', onClick: handleShowInvite },
    { type: 'divider' },
    { type: 'cancel', label: t('visit.cancelVisit'), onClick: (row) => setCancelConfirm({ isOpen: true, id: row.id, name: row.visitorFullName }) },
  ], [t, handleEdit, handleShowInvite]);

  const columns = useMemo(() => {
    const withStatus = baseColumns.map((c) => {
      if (c.name !== 'status') return c;
      return {
        ...c,
        options: {
          ...c.options,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            const effective = row.status === 'approved' && new Date(row.validTo) < new Date()
              ? 'expired'
              : row.status;
            const colors = STATUS_COLORS[effective] || STATUS_COLORS.canceled;
            return <StatusChip $bg={colors.bg} $fg={colors.fg}>{statusLabel(row)}</StatusChip>;
          },
        },
      };
    });
    return [
      ...withStatus,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row || row.status === 'canceled' || row.status === 'rejected') return null;
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data, statusLabel]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('visit.edit') : t('visit.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <InviteLinkDialog
        open={inviteDialog.open}
        link={inviteDialog.link}
        smsSent={inviteDialog.smsSent}
        onClose={() => setInviteDialog({ open: false, link: null, smsSent: false })}
      />

      <ConfirmDialog
        isOpen={cancelConfirm.isOpen}
        title={t('visit.cancelTitle')}
        message={t('visit.cancelMessage', { name: cancelConfirm.name })}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      {useMemo(() => (
        <DataTable
          title={t('visit.myTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
